import * as React from "react";
import { twMerge } from "tailwind-merge";

type TextLevel = 1 | 2 | 3 | 4 | 5 | "p" | "b" | "a";

export type TextProps<TAs extends React.ElementType = "p"> = {
  as?: TAs;
  level?: TextLevel;
  className?: string;
  children?: React.ReactNode;
} & Omit<React.ComponentPropsWithoutRef<TAs>, "as" | "children" | "className">;

const levelClass: Record<TextLevel, string> = {
  1: "font-[700] text-[24px] leading-[1.0] tracking-[0px] align-middle",
  2: "font-semibold font-[600] text-[20px] leading-[28px] tracking-[0]",
  3: "font-[600] text-[18px] leading-[26px] tracking-[0]",
  4: "font-[500] text-[16px] leading-[1.0] tracking-[-0.03em]",
  5: "text-[12px] leading-[16px] tracking-[0]",
  p: "text-[14px] leading-[22px] tracking-[0]",
  b: "text-sm md:text-base font-semibold text-text-primary",
  a: "text-[14px] leading-[22px] tracking-[0] font-[600] underline hover:opacity-90",
};

function Text<TAs extends React.ElementType = "p">({
  as,
  level = "p",
  className,
  children,
  ...rest
}: TextProps<TAs>) {
  const Comp = (as ?? (level === "a" ? "a" : "p")) as React.ElementType;
  return (
    <Comp
      className={twMerge("font-sans font-normal", levelClass[level], className)}
      {...rest}
    >
      {children}
    </Comp>
  );
}

export default Text;
